// frontend/src/components/QrPayloadFields.jsx
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFieldArray } from "react-hook-form";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

/** Selectable QR types — value matches the backend `qr_type` discriminator. */
export const QR_TYPES = [
  { value: "url", label: "URL" },
  { value: "wifi", label: "WiFi" },
  { value: "vcard", label: "vCard" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "plain_text", label: "Plain Text" },
  // Phase D — sent via `content_json` (see PHASE_D_TYPES below), not `payload`.
  { value: "pdf", label: "PDF", group: "More types" },
  { value: "video", label: "Video", group: "More types" },
  { value: "menu", label: "Menu", group: "More types" },
  { value: "feedback", label: "Feedback form", group: "More types" },
  { value: "app_store", label: "App download", group: "More types" },
  { value: "location", label: "Location", group: "More types" },
  { value: "event", label: "Event", group: "More types" },
  { value: "coupon", label: "Coupon", group: "More types" },
  { value: "social_links", label: "Social links", group: "More types" },
];

/**
 * Phase D qr_types: validated `content_json` dicts (no `type` discriminator),
 * as opposed to the 6 typed `payload` union members above (P23-G6).
 */
export const PHASE_D_TYPES = new Set([
  "pdf",
  "video",
  "menu",
  "feedback",
  "app_store",
  "location",
  "event",
  "coupon",
  "social_links",
]);

export function isPhaseDType(qrType) {
  return PHASE_D_TYPES.has(qrType);
}

/** The 5 Phase D types that redirect via a single stored URL. */
const PHASE_D_REDIRECT_TYPES = {
  pdf: "PDF link",
  video: "Video link",
  menu: "Menu link",
  feedback: "Feedback form link",
  app_store: "App download link",
};

const INPUT_CLS =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const LABEL_CLS = "block text-sm font-medium text-gray-700 mb-1";
const ERROR_CLS = "mt-1 text-xs text-red-600";

/**
 * Build the typed `payload` object for a qr_type from flat form values,
 * shaped to match the backend Pydantic schema (`schemas/qr_code.py`).
 *
 * @param {string} qrType
 * @param {object} v  Raw react-hook-form values
 * @returns {object}  A payload with a matching `type` discriminator
 */
export function buildPayload(qrType, v) {
  switch (qrType) {
    case "wifi":
      return {
        type: "wifi",
        ssid: v.ssid,
        password: v.password || "",
        security: v.security || "WPA",
        hidden: !!v.hidden,
      };
    case "vcard":
      return {
        type: "vcard",
        first_name: v.first_name,
        last_name: v.last_name,
        phone: v.phone || "",
        email: v.email || "",
        company: v.company || "",
        title: v.title || "",
        url: v.website || "",
        note: v.note || "",
      };
    case "email":
      return {
        type: "email",
        to: v.to,
        subject: v.subject || "",
        body: v.body || "",
      };
    case "phone":
      return { type: "phone", number: v.number };
    case "plain_text":
      return { type: "plain_text", text: v.plain_text };
    // Phase D types build a bare content_json dict — no `type` key, matching
    // the backend wire contract (schemas/qr_code.py's _PHASE_D_*_CONTENT
    // models don't have a discriminator field).
    case "pdf":
    case "video":
    case "menu":
    case "feedback":
    case "app_store":
      return { url: v.phase_d_url };
    case "location":
      return v.location_mode === "coords"
        ? {
            lat: v.location_lat !== "" ? Number(v.location_lat) : undefined,
            lng: v.location_lng !== "" ? Number(v.location_lng) : undefined,
          }
        : { address: v.location_address || undefined };
    case "event":
      return {
        title: v.event_title,
        starts_at: v.event_starts_at || undefined,
        location: v.event_location || undefined,
      };
    case "coupon":
      return {
        code: v.coupon_code || undefined,
        description: v.coupon_description || undefined,
      };
    case "social_links":
      return {
        links: (v.social_links || [])
          .filter((l) => l && (l.label || l.url))
          .map((l) => ({ label: l.label, url: l.url })),
      };
    case "url":
    default:
      return { type: "url", destination: v.destination };
  }
}

/**
 * Build the request body's payload-carrying field for a qr_type — the 6
 * typed types send `payload` (with `type` embedded); the 9 Phase D types
 * send `content_json` (a bare dict) instead, per the backend wire contract.
 * Shared by CreateCodeModal and EditCodeModal so the payload/content_json
 * branch lives in one place.
 *
 * @param {string} qrType
 * @param {object} v
 * @returns {{payload: object} | {content_json: object}}
 */
export function buildPayloadField(qrType, v) {
  return isPhaseDType(qrType)
    ? { content_json: buildPayload(qrType, v) }
    : { payload: buildPayload(qrType, v) };
}

/**
 * Flatten a stored payload dict into react-hook-form default values for the
 * given qr_type (the inverse of {@link buildPayload}).
 *
 * @param {string} qrType
 * @param {object} payload  The stored payload (QRCodeRead.payload)
 * @returns {object}  Flat default values keyed by field name
 */
export function payloadToDefaults(qrType, payload = {}) {
  const p = payload || {};
  switch (qrType) {
    case "wifi":
      return {
        ssid: p.ssid ?? "",
        password: p.password ?? "",
        security: p.security ?? "WPA",
        hidden: !!p.hidden,
      };
    case "vcard":
      return {
        first_name: p.first_name ?? "",
        last_name: p.last_name ?? "",
        phone: p.phone ?? "",
        email: p.email ?? "",
        company: p.company ?? "",
        title: p.title ?? "",
        website: p.url ?? "",
        note: p.note ?? "",
      };
    case "email":
      return { to: p.to ?? "", subject: p.subject ?? "", body: p.body ?? "" };
    case "phone":
      return { number: p.number ?? "" };
    case "plain_text":
      return { plain_text: p.text ?? "" };
    // Phase D types: `p` is the stored payload dict, which also carries a
    // `type` key added by the backend (services/codes.py) — ignored here,
    // only the real content fields are mapped back to form defaults.
    case "pdf":
    case "video":
    case "menu":
    case "feedback":
    case "app_store":
      return { phase_d_url: p.url ?? "" };
    case "location":
      return {
        location_mode: p.address ? "address" : "coords",
        location_address: p.address ?? "",
        location_lat: p.lat ?? "",
        location_lng: p.lng ?? "",
      };
    case "event":
      return {
        event_title: p.title ?? "",
        event_starts_at: p.starts_at ?? "",
        event_location: p.location ?? "",
      };
    case "coupon":
      return {
        coupon_code: p.code ?? "",
        coupon_description: p.description ?? "",
      };
    case "social_links":
      return {
        social_links: Array.isArray(p.links) && p.links.length
          ? p.links.map((l) => ({ label: l?.label ?? "", url: l?.url ?? "" }))
          : [{ label: "", url: "" }],
      };
    case "url":
    default:
      return { destination: p.destination ?? "" };
  }
}

/** Human labels for each payload field, keyed by qr_type. */
const PAYLOAD_LABELS = {
  url: { destination: "Destination" },
  wifi: {
    ssid: "SSID",
    password: "Password",
    security: "Security",
    hidden: "Hidden network",
  },
  vcard: {
    first_name: "First name",
    last_name: "Last name",
    company: "Company",
    title: "Job title",
    phone: "Phone",
    email: "Email",
    url: "Website",
    note: "Notes",
  },
  email: { to: "To address", subject: "Subject", body: "Body" },
  phone: { number: "Phone number" },
  plain_text: { text: "Text" },
  pdf: { url: "PDF link" },
  video: { url: "Video link" },
  menu: { url: "Menu link" },
  feedback: { url: "Feedback form link" },
  app_store: { url: "App download link" },
  location: { address: "Address", lat: "Latitude", lng: "Longitude" },
  event: { title: "Title", starts_at: "Starts at", location: "Location" },
  coupon: { code: "Code", description: "Description" },
  social_links: { links: "Social links" },
};

/**
 * Compute the human-readable differences between two payloads of the same type.
 *
 * @param {string} qrType
 * @param {object} before  The stored payload
 * @param {object} after   The newly built payload
 * @returns {Array<{label: string, before: string, after: string}>}
 */
export function diffPayload(qrType, before = {}, after = {}) {
  const labels = PAYLOAD_LABELS[qrType] || {};
  const fmt = (v) =>
    v === true
      ? "Yes"
      : v === false
        ? "No"
        : Array.isArray(v)
          ? v.map((l) => `${l?.label ?? ""}: ${l?.url ?? ""}`).join(", ")
          : String(v ?? "");
  return Object.keys(labels).reduce((acc, key) => {
    const b = fmt(before?.[key]);
    const a = fmt(after?.[key]);
    if (b !== a) acc.push({ label: labels[key], before: b, after: a });
    return acc;
  }, []);
}

/**
 * QrPayloadFields — renders the form fields for one QR type.
 *
 * Shared by the create and edit modals so both expose an identical field set.
 * Field *names* (passed to `register`) are stable across types; element ids are
 * namespaced by `idPrefix` so create/edit forms don't collide on the page.
 *
 * `control` and `watch` are only used by the Phase D types (the `location`
 * address/coordinates toggle and the `social_links` repeatable rows) — the
 * 6 typed types don't need them.
 *
 * @param {{
 *   qrType: string,
 *   register: Function,
 *   errors: object,
 *   idPrefix?: string,
 *   control?: object,
 *   watch?: Function
 * }} props
 */
function QrPayloadFields({
  qrType,
  register,
  errors,
  idPrefix = "qr",
  control,
  watch,
}) {
  const id = (suffix) => `${idPrefix}-${suffix}`;
  // Called unconditionally (rules of hooks) — a no-op unless qrType is
  // "social_links", but both modals always pass `control` so this is safe.
  const {
    fields: socialLinkFields,
    append: appendSocialLink,
    remove: removeSocialLink,
  } = useFieldArray({ control, name: "social_links" });
  // CreateCodeModal has no defaultValues for social_links (unlike edit's
  // payloadToDefaults, which always seeds at least one row) — seed one here
  // so a brand-new social_links code isn't stuck showing zero rows.
  useEffect(() => {
    if (qrType === "social_links" && socialLinkFields.length === 0) {
      appendSocialLink({ label: "", url: "" });
    }
  }, [qrType, socialLinkFields.length, appendSocialLink]);

  if (qrType === "wifi") {
    return (
      <>
        <div>
          <label className={LABEL_CLS} htmlFor={id("ssid")}>
            SSID (network name)
          </label>
          <input
            id={id("ssid")}
            type="text"
            className={INPUT_CLS}
            {...register("ssid", { required: "SSID is required." })}
          />
          {errors.ssid && <p className={ERROR_CLS}>{errors.ssid.message}</p>}
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("wifi-password")}>
            Password
          </label>
          <input
            id={id("wifi-password")}
            type="text"
            className={INPUT_CLS}
            {...register("password")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("wifi-security")}>
            Security
          </label>
          <select
            id={id("wifi-security")}
            className={INPUT_CLS}
            {...register("security")}
          >
            <option value="WPA">WPA/WPA2</option>
            <option value="WEP">WEP</option>
            <option value="nopass">None</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            {...register("hidden")}
          />
          Hidden network
        </label>
      </>
    );
  }

  if (qrType === "vcard") {
    return (
      <>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS} htmlFor={id("first-name")}>
              First name
            </label>
            <input
              id={id("first-name")}
              type="text"
              className={INPUT_CLS}
              {...register("first_name", {
                required: "First name is required.",
              })}
            />
            {errors.first_name && (
              <p className={ERROR_CLS}>{errors.first_name.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS} htmlFor={id("last-name")}>
              Last name
            </label>
            <input
              id={id("last-name")}
              type="text"
              className={INPUT_CLS}
              {...register("last_name", { required: "Last name is required." })}
            />
            {errors.last_name && (
              <p className={ERROR_CLS}>{errors.last_name.message}</p>
            )}
          </div>
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("company")}>
            Company
          </label>
          <input
            id={id("company")}
            type="text"
            className={INPUT_CLS}
            {...register("company")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("title")}>
            Job title
          </label>
          <input
            id={id("title")}
            type="text"
            placeholder="e.g. Lead Engineer"
            className={INPUT_CLS}
            {...register("title")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("vcard-phone")}>
            Phone
          </label>
          <input
            id={id("vcard-phone")}
            type="tel"
            className={INPUT_CLS}
            {...register("phone")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("vcard-email")}>
            Email
          </label>
          <input
            id={id("vcard-email")}
            type="email"
            className={INPUT_CLS}
            {...register("email")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("website")}>
            Website
          </label>
          <input
            id={id("website")}
            type="url"
            className={INPUT_CLS}
            {...register("website")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("note")}>
            Notes
          </label>
          <textarea
            id={id("note")}
            rows={3}
            placeholder="Description / comments"
            className={INPUT_CLS}
            {...register("note")}
          />
        </div>
      </>
    );
  }

  if (qrType === "email") {
    return (
      <>
        <div>
          <label className={LABEL_CLS} htmlFor={id("email-to")}>
            To address
          </label>
          <input
            id={id("email-to")}
            type="email"
            placeholder="name@example.com"
            className={INPUT_CLS}
            {...register("to", { required: "Recipient address is required." })}
          />
          {errors.to && <p className={ERROR_CLS}>{errors.to.message}</p>}
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("email-subject")}>
            Subject
          </label>
          <input
            id={id("email-subject")}
            type="text"
            className={INPUT_CLS}
            {...register("subject")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("email-body")}>
            Body
          </label>
          <textarea
            id={id("email-body")}
            rows={3}
            className={INPUT_CLS}
            {...register("body")}
          />
        </div>
      </>
    );
  }

  if (qrType === "phone") {
    return (
      <div>
        <label className={LABEL_CLS} htmlFor={id("phone-number")}>
          Phone number
        </label>
        <input
          id={id("phone-number")}
          type="tel"
          placeholder="+1 555 123 4567"
          className={INPUT_CLS}
          {...register("number", { required: "Phone number is required." })}
        />
        {errors.number && <p className={ERROR_CLS}>{errors.number.message}</p>}
      </div>
    );
  }

  if (qrType === "plain_text") {
    return (
      <div>
        <label className={LABEL_CLS} htmlFor={id("plain-text")}>
          Text
        </label>
        <textarea
          id={id("plain-text")}
          rows={4}
          placeholder="Any text to encode…"
          className={INPUT_CLS}
          {...register("plain_text", { required: "Text is required." })}
        />
        {errors.plain_text && (
          <p className={ERROR_CLS}>{errors.plain_text.message}</p>
        )}
      </div>
    );
  }

  if (PHASE_D_REDIRECT_TYPES[qrType]) {
    const label = PHASE_D_REDIRECT_TYPES[qrType];
    return (
      <div>
        <label className={LABEL_CLS} htmlFor={id("phase-d-url")}>
          {label}
        </label>
        <input
          id={id("phase-d-url")}
          type="url"
          placeholder="https://example.com"
          className={INPUT_CLS}
          {...register("phase_d_url", { required: `${label} is required.` })}
        />
        {errors.phase_d_url && (
          <p className={ERROR_CLS}>{errors.phase_d_url.message}</p>
        )}
      </div>
    );
  }

  if (qrType === "location") {
    const locationMode = watch ? watch("location_mode", "address") : "address";
    return (
      <>
        <div
          role="radiogroup"
          aria-label="Location input mode"
          className="flex gap-4 text-sm text-gray-700"
        >
          <label className="flex items-center gap-2">
            <input
              type="radio"
              value="address"
              {...register("location_mode")}
            />
            Address
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" value="coords" {...register("location_mode")} />
            Coordinates
          </label>
        </div>
        {locationMode === "coords" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLS} htmlFor={id("location-lat")}>
                Latitude
              </label>
              <input
                id={id("location-lat")}
                type="number"
                step="any"
                className={INPUT_CLS}
                {...register("location_lat", {
                  required: "Latitude is required.",
                  min: { value: -90, message: "Latitude must be between -90 and 90." },
                  max: { value: 90, message: "Latitude must be between -90 and 90." },
                })}
              />
              {errors.location_lat && (
                <p className={ERROR_CLS}>{errors.location_lat.message}</p>
              )}
            </div>
            <div>
              <label className={LABEL_CLS} htmlFor={id("location-lng")}>
                Longitude
              </label>
              <input
                id={id("location-lng")}
                type="number"
                step="any"
                className={INPUT_CLS}
                {...register("location_lng", {
                  required: "Longitude is required.",
                  min: { value: -180, message: "Longitude must be between -180 and 180." },
                  max: { value: 180, message: "Longitude must be between -180 and 180." },
                })}
              />
              {errors.location_lng && (
                <p className={ERROR_CLS}>{errors.location_lng.message}</p>
              )}
            </div>
          </div>
        ) : (
          <div>
            <label className={LABEL_CLS} htmlFor={id("location-address")}>
              Address
            </label>
            <input
              id={id("location-address")}
              type="text"
              placeholder="1600 Amphitheatre Pkwy, Mountain View, CA"
              className={INPUT_CLS}
              {...register("location_address", {
                required: "Address is required.",
                maxLength: { value: 500, message: "Max 500 characters." },
              })}
            />
            {errors.location_address && (
              <p className={ERROR_CLS}>{errors.location_address.message}</p>
            )}
          </div>
        )}
      </>
    );
  }

  if (qrType === "event") {
    return (
      <>
        <div>
          <label className={LABEL_CLS} htmlFor={id("event-title")}>
            Title
          </label>
          <input
            id={id("event-title")}
            type="text"
            className={INPUT_CLS}
            {...register("event_title", {
              required: "Title is required.",
              maxLength: { value: 200, message: "Max 200 characters." },
            })}
          />
          {errors.event_title && (
            <p className={ERROR_CLS}>{errors.event_title.message}</p>
          )}
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("event-starts-at")}>
            Starts at
          </label>
          <input
            id={id("event-starts-at")}
            type="datetime-local"
            className={INPUT_CLS}
            {...register("event_starts_at")}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("event-location")}>
            Location
          </label>
          <input
            id={id("event-location")}
            type="text"
            placeholder="e.g. Main Hall, 123 Market St"
            className={INPUT_CLS}
            {...register("event_location", {
              maxLength: { value: 300, message: "Max 300 characters." },
            })}
          />
          {errors.event_location && (
            <p className={ERROR_CLS}>{errors.event_location.message}</p>
          )}
        </div>
      </>
    );
  }

  if (qrType === "coupon") {
    const atLeastOneMsg = "Provide a code and/or a description.";
    return (
      <>
        <div>
          <label className={LABEL_CLS} htmlFor={id("coupon-code")}>
            Code
          </label>
          <input
            id={id("coupon-code")}
            type="text"
            placeholder="e.g. SAVE20"
            className={INPUT_CLS}
            {...register("coupon_code", {
              validate: (v) =>
                !!v || !!watch?.("coupon_description") || atLeastOneMsg,
            })}
          />
          {errors.coupon_code && (
            <p className={ERROR_CLS}>{errors.coupon_code.message}</p>
          )}
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={id("coupon-description")}>
            Description
          </label>
          <textarea
            id={id("coupon-description")}
            rows={3}
            className={INPUT_CLS}
            {...register("coupon_description", {
              validate: (v) =>
                !!v || !!watch?.("coupon_code") || atLeastOneMsg,
            })}
          />
          {errors.coupon_description && (
            <p className={ERROR_CLS}>{errors.coupon_description.message}</p>
          )}
        </div>
      </>
    );
  }

  if (qrType === "social_links") {
    return (
      <div>
        <span className={LABEL_CLS}>Social links</span>
        <div className="space-y-3">
          {socialLinkFields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-2">
              <div className="grid flex-1 grid-cols-2 gap-2">
                <div>
                  <input
                    type="text"
                    aria-label={`Link ${index + 1} label`}
                    placeholder="Label (e.g. Instagram)"
                    className={INPUT_CLS}
                    {...register(`social_links.${index}.label`, {
                      required: "Label is required.",
                      maxLength: { value: 64, message: "Max 64 characters." },
                    })}
                  />
                  {errors.social_links?.[index]?.label && (
                    <p className={ERROR_CLS}>
                      {errors.social_links[index].label.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="url"
                    aria-label={`Link ${index + 1} URL`}
                    placeholder="https://instagram.com/yourhandle"
                    className={INPUT_CLS}
                    {...register(`social_links.${index}.url`, {
                      required: "URL is required.",
                    })}
                  />
                  {errors.social_links?.[index]?.url && (
                    <p className={ERROR_CLS}>
                      {errors.social_links[index].url.message}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeSocialLink(index)}
                disabled={socialLinkFields.length <= 1}
                aria-label={`Remove link ${index + 1}`}
                className="mt-1 shrink-0 rounded-lg p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-gray-400"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => appendSocialLink({ label: "", url: "" })}
          disabled={socialLinkFields.length >= 20}
          className="mt-3 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-40"
        >
          <PlusIcon className="h-4 w-4" />
          Add link
        </button>
      </div>
    );
  }

  // url (default)
  return (
    <div>
      <label className={LABEL_CLS} htmlFor={id("destination")}>
        Destination URL
      </label>
      <input
        id={id("destination")}
        type="url"
        placeholder="https://example.com"
        className={INPUT_CLS}
        {...register("destination", {
          required: "Destination URL is required.",
        })}
      />
      {errors.destination && (
        <p className={ERROR_CLS}>{errors.destination.message}</p>
      )}
    </div>
  );
}

QrPayloadFields.propTypes = {
  qrType: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  idPrefix: PropTypes.string,
  control: PropTypes.object,
  watch: PropTypes.func,
};

export { INPUT_CLS, LABEL_CLS, ERROR_CLS };
export default QrPayloadFields;
